import { TypedDocumentNode, gql } from '@apollo/client';
import { faChevronRight, faLinkSimple } from '@fortawesome/pro-solid-svg-icons';
import { FormattedMessage } from 'react-intl';
import { generatePath, useNavigate } from 'react-router-dom';
import styled from 'styled-components';

import Button from '@sorare/core/src/atoms/buttons/Button';
import { FontAwesomeIcon } from '@sorare/core/src/atoms/icons';
import {
  Horizontal,
  SBHorizontal,
  Vertical,
} from '@sorare/core/src/atoms/layout/flex';
import { BodyM, HeadlineL, LabelM } from '@sorare/core/src/atoms/typography';
import {
  FOOTBALL_PLAY_RIVALS_LEAGUES_SLUG_MEMBERS,
  FOOTBALL_PLAY_RIVALS_LEAGUES_SLUG_SETTINGS_AVATAR,
} from '@sorare/core/src/constants/__generated__/routes';
import { useCurrentUserContext } from '@sorare/core/src/contexts/currentUser';
import { useIsDesktopAndAbove } from '@sorare/core/src/hooks/device/useIsDesktopAndAbove';
import { navLabels } from '@sorare/core/src/lib/glossary';
import { Color } from '@sorare/core/src/style/types';

import { GroupAvatar } from 'components/rivalsUserGroup/GroupAvatar';

import { DeleteLeague } from './DeleteLeague';
import { LeagueCode } from './LeagueCode';
import { LeaveLeague } from './LeaveLeague';
import { Settings_rivalsUserGroup } from './__generated__/index.graphql';

const Title = styled(HeadlineL)`
  padding-left: var(--double-unit);
`;

const ItemWrapper = styled.div`
  & + & {
    border-top: 1px solid var(--c-neutral-300);
  }
  & > * {
    justify-content: space-between;
  }
`;

const MembersButtonLabel = styled(SBHorizontal)`
  width: 100%;
`;

const ButtonText = styled(BodyM)<{ color?: Color }>`
  color: ${({ color }) => color || 'var(--c-neutral-1000)'};
`;

type Props = {
  rivalsUserGroup: Nullable<Settings_rivalsUserGroup>;
  withoutTitle?: boolean;
  userGroupSlug: string;
  onMembersClick?: () => void;
  onAvatarClick?: () => void;
};

const RotatedIcon = styled(FontAwesomeIcon)`
  transform: translateY(var(--unit)) rotate(-45deg);
  transform-origin: top left;
`;
export const Settings = ({
  rivalsUserGroup,
  withoutTitle,
  userGroupSlug,
  onMembersClick = () => {},
  onAvatarClick = () => {},
}: Props) => {
  const { currentUser } = useCurrentUserContext();
  const isAdmin = rivalsUserGroup?.administrator.slug === currentUser?.slug;
  const isDesktopAndAbove = useIsDesktopAndAbove();
  const navigate = useNavigate();

  if (!rivalsUserGroup) {
    return null;
  }

  return (
    <Vertical>
      {!withoutTitle && (
        <Title>
          <FormattedMessage {...navLabels.settings} />
        </Title>
      )}
      <Vertical gap={0}>
        <ItemWrapper>
          <Button
            size="large"
            color="transparent"
            externalLink
            href="https://sorare.com/blog/sorare-rivals-leagues-x-european-championship-official-rules"
          >
            <ButtonText>
              <FormattedMessage
                id="RivalsUserGroup.Settings.howItWorks"
                defaultMessage="How it works & game rules"
              />
            </ButtonText>
            <RotatedIcon icon={faLinkSimple} />
          </Button>
        </ItemWrapper>
        <ItemWrapper>
          <LeagueCode joinSecret={rivalsUserGroup.joinSecret} />
        </ItemWrapper>
        {isAdmin && (
          <ItemWrapper>
            <Button
              size="large"
              color="transparent"
              fullWidth
              onClick={() => {
                if (isDesktopAndAbove) {
                  onAvatarClick();
                } else {
                  navigate(
                    generatePath(
                      FOOTBALL_PLAY_RIVALS_LEAGUES_SLUG_SETTINGS_AVATAR,
                      {
                        slug: userGroupSlug,
                      }
                    )
                  );
                }
              }}
            >
              <ButtonText>
                <FormattedMessage
                  id="RivalsUserGroup.Settings.avatar"
                  defaultMessage="Avatar"
                />
              </ButtonText>
              <Horizontal center>
                <GroupAvatar userGroup={rivalsUserGroup} size={24} />
                <FontAwesomeIcon icon={faChevronRight} />
              </Horizontal>
            </Button>
          </ItemWrapper>
        )}
        <ItemWrapper>
          <Button
            color="transparent"
            size="large"
            fullWidth
            onClick={() => {
              if (isDesktopAndAbove) {
                onMembersClick();
              } else {
                navigate(
                  generatePath(FOOTBALL_PLAY_RIVALS_LEAGUES_SLUG_MEMBERS, {
                    slug: userGroupSlug,
                  })
                );
              }
            }}
          >
            <MembersButtonLabel>
              <ButtonText>
                <FormattedMessage {...navLabels.members} />
              </ButtonText>
              <Horizontal center>
                <LabelM color="var(--c-neutral-600)">
                  {rivalsUserGroup.membershipsCount}
                </LabelM>
                <FontAwesomeIcon icon={faChevronRight} />
              </Horizontal>
            </MembersButtonLabel>
          </Button>
        </ItemWrapper>
        {!isAdmin && (
          <ItemWrapper>
            <LeaveLeague id={rivalsUserGroup.id} />
          </ItemWrapper>
        )}
        {isAdmin && (
          <ItemWrapper>
            <DeleteLeague id={rivalsUserGroup.id} />
          </ItemWrapper>
        )}
      </Vertical>
    </Vertical>
  );
};

Settings.fragments = {
  rivalsUserGroup: gql`
    fragment Settings_rivalsUserGroup on RivalsUserGroup {
      id
      slug
      joinSecret
      administrator {
        slug
      }
      membershipsCount
      ...GroupAvatar_rivalsUserGroup
    }
    ${GroupAvatar.fragments.rivalsUserGroup}
  ` as TypedDocumentNode<Settings_rivalsUserGroup>,
};
